import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Form, Dropdown } from 'semantic-ui-react'
import { useApiQuery } from '../../../../../hooks/useApiQuery.js'

/**
 * UserSelectFilter
 *
 * Searchable user select filter.
 * Used for Driver and Created By filters.
 */
export const UserSelectFilter = ({
  value,
  onChange,
  userRole,
  hasCreatedOrders,
  multiple,
  placeholder,
  label,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOptions, setSelectedOptions] = useState([])

  // Build query params
  const queryParams = {
    query: searchQuery,
    is_active: null,
    ignore_active_filter: true,
    page_size: 50,
  }

  if (userRole) {
    queryParams.user_role = userRole
  }

  if (hasCreatedOrders) {
    queryParams.has_created_orders = true
  }

  // Fetch users based on search
  const { data: usersResponse, isLoading } = useApiQuery({
    key: ['searchUsers', userRole, hasCreatedOrders, searchQuery],
    payload: queryParams,
    enabled: searchQuery.length >= 2,
    staleTime: 30000,
  })

  const users = usersResponse?.results || []

  // Format options for dropdown and merge with previously selected
  const mergedOptions = useMemo(() => {
    const searchOptions = users.map((user) => {
      const displayName =
        user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim()
      const companyName = user.companyName ? ` (${user.companyName})` : ''

      return {
        key: user.uid,
        value: user.uid,
        text: `${displayName}${companyName}`,
        data: user,
      }
    })
    const searchUids = new Set(searchOptions.map((o) => o.value))
    const preserved = selectedOptions.filter((o) => !searchUids.has(o.value))
    return [...preserved, ...searchOptions]
  }, [users, selectedOptions])

  // Handle search change
  const handleSearchChange = useCallback((e, { searchQuery: query }) => {
    setSearchQuery(query)
  }, [])

  // Handle selection change
  //
  // Selections are resolved against `mergedOptions` (current results + the
  // options we have preserved across searches), never against `users` (the
  // current result set only). This keeps a previously-selected user resolvable
  // after a new search drops it from the results: in single-select it returns
  // the full object instead of clearing to null, and in multi-select it keeps
  // the full object instead of degrading to a bare { uid }. Reusing the merged
  // option also reuses its already-built display text, so the display-name
  // chain lives in one place (the mergedOptions memo).
  const handleChange = useCallback(
    (e, { value: selectedValue }) => {
      if (multiple) {
        const newSelectedOpts = selectedValue.map((uid) => {
          const existing = mergedOptions.find((o) => o.value === uid)
          return existing || { key: uid, value: uid, text: uid, data: { uid } }
        })
        setSelectedOptions(newSelectedOpts)
        onChange(newSelectedOpts.map((o) => o.data))
      } else {
        const existing = mergedOptions.find((o) => o.value === selectedValue)
        if (existing) {
          setSelectedOptions([existing])
        }
        onChange(existing ? existing.data : null)
      }
    },
    [mergedOptions, multiple, onChange],
  )

  // Get current value for dropdown
  const dropdownValue = multiple
    ? (value || []).map((v) => v.uid || v)
    : value?.uid || value || ''

  return (
    <Form.Field>
      <label>{label}</label>
      <Dropdown
        placeholder={placeholder}
        fluid
        selection
        search
        multiple={multiple}
        options={mergedOptions}
        value={dropdownValue}
        onChange={handleChange}
        onSearchChange={handleSearchChange}
        loading={isLoading}
        noResultsMessage={
          searchQuery.length < 2 ? 'Type to search...' : 'No results found'
        }
      />
    </Form.Field>
  )
}

UserSelectFilter.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  onChange: PropTypes.func.isRequired,
  userRole: PropTypes.string,
  hasCreatedOrders: PropTypes.bool,
  multiple: PropTypes.bool,
  placeholder: PropTypes.string,
  label: PropTypes.string,
}

UserSelectFilter.defaultProps = {
  value: null,
  userRole: null,
  hasCreatedOrders: false,
  multiple: false,
  placeholder: 'Search users...',
  label: 'User',
}
